import {
  getModel,
  getNodeCo2MaterialTypeId,
  getNodeInScopeTotalCo2,
  getProcessableNodesForModel,
  type ProjectAnalysisInput,
  type ProjectAnalysisModelEntry,
  type ProjectAnalysisScenarioEntry,
} from "@/types/analysis/ProjectAnalysisInput";
import { createNodeMaterialKey } from "@/types/LCAoM/ModelsDataService";
import { ColorDefinition, DataVisualization } from "@/types/colors";
import type { INodeDto } from "@/types/INodeDto";
import { SankeyOptions } from "@/types/sankeyOptions";
import { SelectionManager } from "@/types/SelectionManager";
import { CategoryData, Co2MaterialType, NodeMaterialDto } from "@api/apiClient";
import "@api/apiClient-extensions";
import i18n from "@i18n/i18n";
import bucketize, { Buckets } from "@utils/bucketize";
import { findMapKey } from "@utils/mapUtils";
import _ from "lodash";
import mitt from "mitt";

// @ts-ignore

export { };
declare global {
  interface Window {
    ProjectAnalysis: ProjectAnalysis;
  }
}

export enum GroupType {
  Scenario = 'Scenario',
  Model = 'Model'
}

export enum AnalysisType {
  CadModelCategory = 'CadModelCategory',
  CadMaterial = 'CadMaterial',
  CadMaterialCategory = 'CadMaterialCategory', // WIP
  Co2MaterialType = 'Co2MaterialType',
  Co2MaterialCategory = 'Co2MaterialCategory', // WIP
  ModelGroup = 'ModelGroup', // WIP
  Co2eIntensity = 'Co2eIntensity',
  Sankey = 'Sankey',
  /**
   * For mapping viewer selections to analysis selections.
   */
  LmViewer = 'LmViewer',
  System = "System",
  SubSystem = "SubSystem",
  Attribute1 = "Attribute1",
  Attribute2 = "Attribute2",
  Attribute3 = "Attribute3",
}

export const ElementPropertyToAnalysisType = new Map<string, AnalysisType>([
  ['cadModelCategoryId', AnalysisType.CadModelCategory],
  ['cadMaterial', AnalysisType.CadMaterial],
  ['cadMaterialCategoryId', AnalysisType.CadMaterialCategory],
  ['co2MaterialTypeId', AnalysisType.Co2MaterialType],
  ['co2MaterialCategoryId', AnalysisType.Co2MaterialCategory],
  ['modelGroupId', AnalysisType.ModelGroup],
  ['co2eIntensity', AnalysisType.Co2eIntensity],
  ['system', AnalysisType.System],
  ['subSystem', AnalysisType.SubSystem],
  ['freeText1', AnalysisType.Attribute1],
  ['freeText2', AnalysisType.Attribute2],
  ['freeText3', AnalysisType.Attribute3],
]);

/** Property keys used for analysis (same as ElementPropertyToAnalysisType keys); nodes are INodeDto. */
export type AnalysisPropertyKey =
  | 'cadModelCategoryId' | 'cadMaterial' | 'cadMaterialCategoryId' | 'co2MaterialTypeId' | 'co2MaterialCategoryId'
  | 'modelGroupId' | 'co2eIntensity' | 'system' | 'subSystem' | 'freeText1' | 'freeText2' | 'freeText3';

export enum UnitType {
  tCO2e = 'tCO2e',
  KgCO2ePerM2 = 'KgCO2e/m2',
  KgCO2ePerM2PerYear = 'KgCO2e/m2/year',
}

export type AnalysisSelectionType = AnalysisType;

export type ScenarioId = string;
export type ModelId = string;
export type GroupKey = number | string;
export type HexColor = string;
export type CadIds = Array<string>;
export type Urn = string;

export type ModelBucketCadIds = Map<string, Map<number, Array<string>>>;

const EmptyGuid = '00000000-0000-0000-0000-000000000000';
const TOTALCO2_BUCKET_SIZE = 6;

export class AnalysisItem {
  /**
   * @remarks
   * oneOf: 
   *  cadModelCategoryId
   *  cadMaterial
   *  cadMaterialCategoryId
   *  co2MaterialTypeId
   *  co2MaterialCategoryId
   *  modelGroupId (aka scopeId)
   *  co2eIntensity
   */
  groupKey: GroupKey = '';
  name: string = '';
  elementCount: number = 0;
  lengthTotal: number = 0;
  areaTotal: number = 0;
  volumeTotal: number = 0;
  //massTotal: number = 0;
  //quantityTotal: number = 0;
  co2eTotal: number = 0;
  minCo2e: number = 0;
  maxCo2e: number = 0;

  constructor(groupKey: GroupKey, name: string) {
    this.groupKey = groupKey;
    this.name = name;
  }
}

export class GroupAnalysis {
  analysisType: AnalysisType = AnalysisType.CadModelCategory;
  groupType: GroupType = GroupType.Scenario;
  groupIndex: number = 0;

  constructor(id: ModelId | ScenarioId, groupIndex: number, name: string, analysisType: AnalysisType, groupType: GroupType) {
    this.id = id;
    this.groupIndex = groupIndex;
    this.name = name;
    this.analysisType = analysisType;
    this.groupType = groupType;
  }

  /**
   * @remarks
   * modelId or scenarioId
   */
  id: ModelId | ScenarioId = '';
  /**
   * @remarks
   * name of the model or scenario
   */
  name: string = '';
  items: Map<GroupKey, AnalysisItem> = new Map<GroupKey, AnalysisItem>();
  /** 
   * @remarks
   * sum of all items.co2eTotal
   */
  co2eTotal: number = 0;
}

export class ScenarioAnalysis {
  scenarioId: ScenarioId = '';
  scenarioIndex: number = 0;
  name: string = '';
  groupType: GroupType = GroupType.Scenario;
  totalFloorArea?: number;
  serviceLife?: number;

  constructor(scenarioId: ScenarioId, scenarioIndex: number, name: string, groupType: GroupType, totalFloorArea?: number, serviceLife?: number) {
    this.scenarioId = scenarioId;
    this.scenarioIndex = scenarioIndex;
    this.name = name;
    this.groupType = groupType;
    this.totalFloorArea = totalFloorArea;
    this.serviceLife = serviceLife;
    this.co2eIntensity = new GroupAnalysis(scenarioId, scenarioIndex, name, AnalysisType.Co2eIntensity, GroupType.Scenario);
  }

  /**
   * @remarks
   * Contains AnalysisType.Co2eIntensity type analysis always by *Scenario* to be used in the TotalCO2 viewer extension.
   * The GroupAnalysis.Id will be a scenarioId.
   * Will need to be updated when model selection changes for a given scenario from the results grid or when
   * switching views.
   */
  co2eIntensity: GroupAnalysis;
  /**
   * @remarks
   * Contains the GroupAnalysis results. 
   * If GroupType.Scenario, then there will be a single aggregate GroupAnalysis for all selected models in the scenario.
   * If GroupType.Model, then there will be a GroupAnalysis for each selected model in the scenario.
   */
  groupData: Array<GroupAnalysis> = [];
  /**
   * @remarks
   * Contains the color assignment for each row in the results grid represented by GroupAnalysis.items[]
   */
  groupColorMap: Map<GroupKey, ColorDefinition> = new Map<GroupKey, ColorDefinition>();
  /**
   * @remarks
   * Contains the associated cadIds for each row in the results grid represented by GroupAnalysis.items[]
   * grouped by model.
   */
  modelGroupCadIdsMap: Map<Urn, Map<GroupKey, CadIds>> = new Map<Urn, Map<GroupKey, CadIds>>();
  /**
   * cadId -> hex color for viewer highlighting when using NodeDto-based input (no elementDatas to mutate).
   */
  cadIdToHexColorMap: Map<string, string> = new Map<string, string>();
}

export class AnalysisData {
  analysisType: AnalysisType = AnalysisType.CadModelCategory;
  groupType: GroupType = GroupType.Scenario;
  /**
   * @remarks
   * usage: scenarioAnalyses[scenarioId].co2eIntensity
   * 
   * or
   * 
   * usage: scenarioAnalyses[scenarioId].groupData
   */
  scenarioAnalyses: Map<ScenarioId, ScenarioAnalysis> = new Map<ScenarioId, ScenarioAnalysis>();

  constructor(analysisType: AnalysisType, groupType: GroupType) {
    this.analysisType = analysisType;
    this.groupType = groupType;
  }

  getGroupKeys = (): Array<GroupKey> => {
    const items = Array.from(this.scenarioAnalyses.values())[0]?.groupData[0]?.items;
    return items ? Array.from(items.keys()) : [];
  };

  getItemNames = (): Array<GroupKey> => {
    const scenarioEntry = this.scenarioAnalyses.entries().next().value;
    if (!scenarioEntry) {
      return [];
    }
    const groupDataEntry = scenarioEntry[1].groupData.entries().next().value;
    if (!groupDataEntry) {
      return [];
    }
    const items: Map<GroupKey, AnalysisItem> = groupDataEntry[1].items;
    return Array.from(items).map(([_key, value]) => value.name);
  };

  getFilteredItems = (filter: (item: AnalysisItem) => boolean): Array<AnalysisItem> => {
    const scenarioEntry = this.scenarioAnalyses.entries().next().value;
    if (!scenarioEntry) {
      return [];
    }
    const groupDataEntry = scenarioEntry[1].groupData.entries().next().value;
    if (!groupDataEntry) {
      return [];
    }
    const items: Map<GroupKey, AnalysisItem> = groupDataEntry[1].items;
    const values = Array.from(items).map(([_key, value]) => value);
    return values.filter((item: AnalysisItem) => filter(item));
  };

  colorDefinitions = (): Array<ColorDefinition> => {
    const scenarioEntry = this.scenarioAnalyses.entries().next().value;
    if (!scenarioEntry) {
      return [];
    }
    const groupColorMap: Map<GroupKey, ColorDefinition> = scenarioEntry[1].groupColorMap;
    return Array.from(groupColorMap.entries()).map(([_key, value]) => value);
  };

  get groupTitle() {
    switch (this.analysisType) {
      case AnalysisType.CadModelCategory:
        return i18n.t('analysis.group-title.category');
      case AnalysisType.CadMaterial:
        return i18n.t('analysis.group-title.cad-material');
      case AnalysisType.CadMaterialCategory:
        return i18n.t('analysis.group-title.cad-material-category');
      case AnalysisType.Co2MaterialType:
        return i18n.t('analysis.group-title.co2-material-type');
      case AnalysisType.Co2MaterialCategory:
        return i18n.t('analysis.group-title.co2-material-category');
      case AnalysisType.ModelGroup:
        return i18n.t('analysis.group-title.model-group');
      case AnalysisType.Co2eIntensity:
        return i18n.t('analysis.group-title.co2-intensity');
      case AnalysisType.Sankey:
        return i18n.t('analysis.group-title.sankey');
      default:
        return i18n.t('analysis.group-title.default');
    }
  }
}

export class SankeyData {
  graph = {
    nodes: [],
    links: [],
  };

  constructor(graph: any) {
    this.graph = graph;
  }
}

export enum ProjectAnalysisEvents {
  ANALYSIS_CHANGED_EVENT = "analysisChanged",
  SANKEY_ANALYSIS_CHANGED_EVENT = "sankeyAnalysisChanged",
  SELECTION_CHANGED_EVENT = "selectionChanged",
  CONFIGURATION_CHANGED_EVENT = "configurationChanged",
  UNIT_SELECTION_CHANGED_EVENT = "unitSelectionChanged"
}

/**
 * The `ProjectAnalysis` class is responsible for managing and updating analysis data for a project.
 * It handles various types of analyses, including scenario, CAD model category, CAD material, CO2 material type, and more.
 * The class also manages selections and emits events when analyses or selections change.
 * 
 * ProjectAnalysis UML Diagrams:
 * - https://lucid.app/lucidchart/471e987d-7685-479d-9e80-0b49b339c616/edit?viewport_loc=-7180%2C-3003%2C22560%2C11339%2CHWEp-vi-RSFO&invitationId=inv_0a778aba-3970-4926-994f-3f02e8e27603
 * - https://lucid.app/lucidchart/471e987d-7685-479d-9e80-0b49b339c616/view?page=HWEp-vi-RSFO#
 * 
 * @class ProjectAnalysis
 * 
 * @property {GroupType} groupType - The type of group for the analysis, defaulting to `GroupType.Scenario`.
 * @property {AnalysisType} analysisType - The type of analysis, defaulting to `AnalysisType.CadModelCategory`.
 * @property {Map<AnalysisType, AnalysisData>} analyses - A map storing analysis data for different analysis types.
 * @property {Map<AnalysisType, Set<GroupKey>>} selections - A map storing selections for different analysis types.
 * @property {Map<string, Set<string>>} modelToCadIdMap - A map storing model IDs to CAD IDs.
 * @property {any} emitter - An event emitter for emitting analysis and selection change events.
 * 
 * @constructor
 * Initializes a new instance of the `ProjectAnalysis` class and assigns it to the global `window` object.
 * 
 * @method updateAnalysis
 * Updates the analysis data based on the current selection, project data, and CO2 material types.
 * Emits an `ANALYSIS_CHANGED_EVENT` after updating the analysis.
 * 
 * @method addAnalysis
 * Adds a new analysis based on the current selection, project data, CO2 material types, and specified analysis type.
 * 
 * @method updateSankeyAnalysis
 * Updates the Sankey analysis data based on the current selection, project data, CO2 material types, and options.
 * Emits a `SANKEY_ANALYSIS_CHANGED_EVENT` after updating the analysis.
 * 
 * @method setGroupType
 * Sets the group type for the analysis and updates the analysis data if the group type has changed.
 * Emits an `ANALYSIS_CHANGED_EVENT` after updating the analysis.
 * 
 * @method setAnalysisType
 * Sets the analysis type and updates the analysis data if the analysis type has changed.
 * 
 * @method addSelection
 * Adds a selection for the specified analysis type based on the current viewer selection, project data, and model-to-CAD ID map.
 * Emits a `SELECTION_CHANGED_EVENT` if the selection has changed.
 * 
 * @method getSelections
 * Retrieves a set of unique model element values for a given property based on the current selections (modelToCadIdMap).
 * 
 * @method getSelectionsFromFilter
 * Retrieves a set of unique property values for a given property based on the current selections.
 */
export class ProjectAnalysis {
  groupType: GroupType = GroupType.Scenario;
  analysisType: AnalysisType = AnalysisType.CadModelCategory;
  analyses: Map<AnalysisType, AnalysisData> = new Map<AnalysisType, AnalysisData>();
  selectedUnitType: UnitType = UnitType.KgCO2ePerM2; //Default value
  currentSelectionType: AnalysisSelectionType | null = null;
  selections: Map<AnalysisType, Set<GroupKey>> = new Map<AnalysisType, Set<GroupKey>>();
  modelToCadIdMap: Map<string, Set<string>> = new Map<string, Set<string>>();

  totalCo2Config: string[] = ['totalCo2'];

  emitter: any = mitt();

  constructor() {
    window.ProjectAnalysis = this;
  }

  get isGroupTypeScenario() {
    return this.groupType === GroupType.Scenario;
  }

  get isGroupTypeModel() {
    return this.groupType === GroupType.Model;
  }

  /**
   * Sets the total CO2 configuration based on the specified columns.
   * @param columns The columns to include in the total CO2 configuration.
   * Valid values are: 'a1A2A3', 'a4A5', 'a', 'b1', 'b2B5', 'b', 'biogenic', 'c', 'd', 'totalCo2'
   */
  setTotalCo2Config = (columns: string[]) => {
    const totalCo2Configurator = new TotalCo2Configurator();
    const newTotalCo2Config = totalCo2Configurator.getTotalCo2Config(columns);

    if (_.isEqual(this.totalCo2Config, newTotalCo2Config) === false) {
      this.totalCo2Config = newTotalCo2Config;
      this.emitter.emit(ProjectAnalysisEvents.CONFIGURATION_CHANGED_EVENT);
    }
  };

  /**
   * Gets the analysis data for the specified analysis type.
   * 
   * @param analysisType The type of analysis to retrieve.
   * @returns The analysis data for the specified analysis type or `undefined` if no analysis data exists.
   */
  getAnalysis = (analysisType: AnalysisType): AnalysisData | undefined => {
    return this.analyses.get(analysisType);
  };

  /**
   * Gets the current selections by analysis type.
   * 
   * @param analysisType The type of analysis to retrieve the selections for.
   * @returns The current selections by analysis type or an empty set if no selections exist.
   */
  getSelections = (analysisType: AnalysisType): Set<GroupKey> => {
    return this.selections.get(analysisType) || new Set();
  };

  // TODO: if a scenario selection changes, update the scenarioAnalysis data
  // TODO: need a scenario selection change detector to compare current
  // scenario selection with the ProjectAnalysis scenario selection (use a hash?)
  // TODO: if any model data or assignment changes, update the analysis data

  updateAnalysis = (
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput | null | undefined,
    co2MaterialTypes: Co2MaterialType[] | null | undefined) => {
    console.log("[ProjectAnalysis] updateAnalysis: ", input);
    // Assumption here is that selection has changed and now all existing data is invalid.
    this.reset();

    this.updateAnalyses(selectionManager, input, co2MaterialTypes, this.analysisType);

    this.emitter.emit(ProjectAnalysisEvents.ANALYSIS_CHANGED_EVENT);
  };

  addAnalysis = (
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput | null | undefined,
    co2MaterialTypes: Co2MaterialType[] | null | undefined,
    analysisType: AnalysisType) => {

    this.updateAnalyses(selectionManager, input, co2MaterialTypes, analysisType);
  };

  setUnitType = (unitType: UnitType) => {
    this.selectedUnitType = unitType;
    this.emitter.emit(ProjectAnalysisEvents.UNIT_SELECTION_CHANGED_EVENT, unitType);
  };

  updateSankeyAnalysis = (
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput | null | undefined,
    co2MaterialTypes: Co2MaterialType[] | null | undefined,
    options: SankeyOptions) => {
    this.analyses.delete(AnalysisType.Sankey);

    this.updateSankeyAnalyses(selectionManager, input, co2MaterialTypes, options);

    this.emitter.emit(ProjectAnalysisEvents.SANKEY_ANALYSIS_CHANGED_EVENT);
  };

  setGroupType = (
    groupType: GroupType,
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput | null | undefined,
    co2MaterialTypes: Co2MaterialType[] | null | undefined) => {
    if (this.groupType !== groupType) {
      this.groupType = groupType;
      this.reset();

      this.updateAnalyses(selectionManager, input, co2MaterialTypes, this.analysisType);

      this.emitter.emit(ProjectAnalysisEvents.ANALYSIS_CHANGED_EVENT);
    }
  };

  setAnalysisType = (
    analysisType: AnalysisType,
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput | null | undefined,
    co2MaterialTypes: Co2MaterialType[] | null | undefined) => {
    if (this.analysisType !== analysisType) {
      this.analysisType = analysisType;

      this.updateAnalyses(selectionManager, input, co2MaterialTypes, this.analysisType);
    }
  };

  setCurrentSelectionType = (analysisSelectionType: AnalysisSelectionType) => {
    this.currentSelectionType = analysisSelectionType;
  };

  addSelections = (
    analysisType: AnalysisType,
    selections: Set<GroupKey>) => {

    switch (analysisType) {
      case AnalysisType.CadModelCategory:
        this.selections.set(AnalysisType.CadModelCategory, selections);
        break;
      case AnalysisType.CadMaterial:
        this.selections.set(AnalysisType.CadMaterial, selections);
        break;
      case AnalysisType.CadMaterialCategory:
        //this.selections.set(AnalysisType.CadMaterial, selections);
        break;
      case AnalysisType.Co2MaterialType:
        this.selections.set(AnalysisType.Co2MaterialType, selections);
        break;
      case AnalysisType.Co2MaterialCategory:
        //this.selections.set(AnalysisType.Co2MaterialCategory, selections);
        break;
      case AnalysisType.Co2eIntensity:
        //this.selections.set(AnalysisType.Co2eIntensity, selections);
        break;
      case AnalysisType.System:
        this.selections.set(AnalysisType.System, selections);
        break;
      case AnalysisType.SubSystem:
        this.selections.set(AnalysisType.SubSystem, selections);
        break;
      case AnalysisType.Attribute1:
        this.selections.set(AnalysisType.Attribute1, selections);
        break;
      case AnalysisType.Attribute2:
        this.selections.set(AnalysisType.Attribute2, selections);
        break;
      case AnalysisType.Attribute3:
        this.selections.set(AnalysisType.Attribute3, selections);
        break;
      default:
        break;
    }

    this.setCurrentSelectionType(analysisType as any);

    this.emitter.emit(ProjectAnalysisEvents.SELECTION_CHANGED_EVENT);
  };

  addSelectionsFromModelToCadIdMap = (
    analysisType: AnalysisType,
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput | null | undefined,
    modelToCadIdMap: Map<string, Set<string>> | null | undefined = null) => {
    let hasSelectionChanged = false;

    // When switching GroupType, we can use the existing modelToCadIdMap
    // to generate a new selection set. When selection changes, we'll provide a new
    // modelToCadIdMap to generate a new selection set.
    if (modelToCadIdMap) {
      this.modelToCadIdMap = modelToCadIdMap;
      this.selections.clear();
      hasSelectionChanged = true;
    }

    switch (analysisType) {
      case AnalysisType.CadModelCategory:
        if (!this.hasSelection(AnalysisType.CadModelCategory)) {
          const selections = this.getSelectedElementsByModelToCadIdMap(selectionManager, input, this.modelToCadIdMap, 'cadModelCategoryId');
          this.selections.set(AnalysisType.CadModelCategory, selections);
        }
        break;
      case AnalysisType.CadMaterial:
        if (!this.hasSelection(AnalysisType.CadMaterial)) {
          const selections = this.getSelectedElementsByModelToCadIdMap(selectionManager, input, this.modelToCadIdMap, 'cadMaterial');
          this.selections.set(AnalysisType.CadMaterial, selections);
        }
        break;
      case AnalysisType.CadMaterialCategory:
        if (!this.hasSelection(AnalysisType.CadMaterialCategory)) {
          //const selections = this.getSelectionsFromModelToCadIdMap(selectionManager, input, this.modelToCadIdMap, 'cadMaterialCategoryId');
          //this.selections.set(AnalysisType.CadMaterial, selections);
        }
        break;
      case AnalysisType.Co2MaterialType:
        if (!this.hasSelection(AnalysisType.Co2MaterialType)) {
          const selections = this.getSelectedElementsByModelToCadIdMap(selectionManager, input, this.modelToCadIdMap, 'co2MaterialTypeId');
          this.selections.set(AnalysisType.Co2MaterialType, selections);
        }
        break;
      case AnalysisType.Co2MaterialCategory:
        if (!this.hasSelection(AnalysisType.Co2MaterialCategory)) {
          //const selections = this.getSelectionsFromModelToCadIdMap(selectionManager, input, this.modelToCadIdMap, 'co2MaterialCategoryId');
          //this.selections.set(AnalysisType.Co2MaterialCategory, selections);
        }
        break;
      case AnalysisType.Co2eIntensity:
        //if (!this.hasSelection(AnalysisType.Co2eIntensity)) {
        //  const selections = this.getSelectionsFromModelToCadIdMap(selectionManager, input, this.modelToCadIdMap, 'totalCo2'); // TODO: this will need some extra lookups to convert totalCo2 to bucketIndex?
        //  this.selections.set(AnalysisType.Co2eIntensity, selections);
        //}
        break;
      case AnalysisType.System:
        if (!this.hasSelection(AnalysisType.System)) {
          const selections = this.getSelectedElementsByModelToCadIdMap(selectionManager, input, this.modelToCadIdMap, 'system');
          this.selections.set(AnalysisType.System, selections);
        }
        break;
      case AnalysisType.SubSystem:
        if (!this.hasSelection(AnalysisType.SubSystem)) {
          const selections = this.getSelectedElementsByModelToCadIdMap(selectionManager, input, this.modelToCadIdMap, 'subSystem');
          this.selections.set(AnalysisType.SubSystem, selections);
        }
        break;
      case AnalysisType.Attribute1:
        if (!this.hasSelection(AnalysisType.Attribute1)) {
          const selections = this.getSelectedElementsByModelToCadIdMap(selectionManager, input, this.modelToCadIdMap, 'freeText1');
          this.selections.set(AnalysisType.Attribute1, selections);
        }
        break;
      case AnalysisType.Attribute2:
        if (!this.hasSelection(AnalysisType.Attribute2)) {
          const selections = this.getSelectedElementsByModelToCadIdMap(selectionManager, input, this.modelToCadIdMap, 'freeText2');
          this.selections.set(AnalysisType.Attribute2, selections);
        }
        break;
      case AnalysisType.Attribute3:
        if (!this.hasSelection(AnalysisType.Attribute3)) {
          const selections = this.getSelectedElementsByModelToCadIdMap(selectionManager, input, this.modelToCadIdMap, 'freeText3');
          this.selections.set(AnalysisType.Attribute3, selections);
        }
        break;
      default:
        break;
    }

    if (hasSelectionChanged) {
      this.emitter.emit(ProjectAnalysisEvents.SELECTION_CHANGED_EVENT);
    }
  };

  /**
   * Retrieves a set of unique model element values for a given property based on the current selections (modelToCadIdMap).
   * 
   * @param selectionManager - The selection manager containing the current scenario and model selections.
   * @param input - The project data containing scenario and model information.
   * @param analysisType - The type of analysis to retrieve selections for (e.g., CadModelCategory, CadMaterial, Co2MaterialType).
   * @returns A set of unique model element values for a given property based on the current selections.
   */
  getSelectedElementsByAnalysisType = (
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput | null | undefined,
    analysisType: AnalysisType): Set<GroupKey> => {
    const currentSelections = projectAnalysis.selections.get(this.currentSelectionType as any) || new Set();
    const filterKey = findMapKey(ElementPropertyToAnalysisType, (key: string) => key === this.currentSelectionType) as AnalysisPropertyKey;
    const property = findMapKey(ElementPropertyToAnalysisType, (key: string) => key === analysisType) as AnalysisPropertyKey;
    const selections = projectAnalysis.getSelectedElementsByFilter(selectionManager, input, property, filterKey, [...currentSelections] as any);
    return selections;
  };


  /**
     * Retrieves a Set of unique property values for a given property
     * 
     * @param input - The project data containing scenario and model information.
     * @param property - The property to retrieve unique values for (e.g., cadModelCategoryId, cadMaterial, co2MaterialTypeId, totalCo2).
     * @param filterKey - The property to filter the results by (e.g., cadModelCategoryId, cadMaterial, co2MaterialTypeId).
     * @param filterValues - The values to filter the results by (e.g., [cadModelCategoryId1, cadModelCategoryId2]).
     * @returns A Set of unique values for the given property based on the current selections.
     */
  getSelectedElementsByFilter = (
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput | null | undefined,
    property: AnalysisPropertyKey,
    _filterKey: AnalysisPropertyKey,
    filterValues: string[] | number[]): Set<GroupKey> => {
    const values: Set<GroupKey> = new Set();

    if (!input) {
      return values;
    }

    const filterArr = filterValues as (string | number)[];
    this.processInputModelData(input, selectionManager, (_scenarioEntry, modelEntry, processableNodes) => {
      const modelId = getModel(modelEntry)?.apsViewId;
      if (modelId) {
        const scenarioId = _scenarioEntry.scenario.scenarioId as string;
        if (typeof property === 'string' && ['system', 'subSystem', 'freeText1', 'freeText2', 'freeText3'].includes(property)) {
          processableNodes.forEach((node) => {
            const val = this.getAttributeFromNode(node, property);
            if (val && filterArr.includes(val)) values.add(val as GroupKey);
          });
        } else {
          processableNodes.forEach((node) => {
            const raw = (node as unknown as Record<string, unknown>)[property as string];
            const val = raw !== undefined && raw !== null ? String(raw) : (property === 'co2MaterialTypeId' ? getNodeCo2MaterialTypeId(node, scenarioId, input!.nodeMaterialLookup, EmptyGuid) : undefined);
            if (val && filterArr.includes(val as string | number)) values.add(val as GroupKey);
          });
        }
      }
    });

    console.log(`${property} values: `, values);
    return values;
  };

  applyUnitConversion = (value: number, scenarioAnalysis: ScenarioAnalysis): number => {

    const unitType = this.selectedUnitType;
    const totalFloorArea = scenarioAnalysis.totalFloorArea || 1;
    const serviceLife = scenarioAnalysis.serviceLife || 1;

    switch (unitType) {
      case UnitType.KgCO2ePerM2:
        return (value * 1000) / totalFloorArea;
      case UnitType.KgCO2ePerM2PerYear:
        return (value * 1000) / (totalFloorArea * serviceLife);
      case UnitType.tCO2e:
        return value; // No conversion
    }
  };

  /**
   * Retrieves a Set of unique Model Element values for a given property based on the current viewer selections (modelToCadIdMap)
   * 
   * @param input - The project data containing scenario and model information.
   * @param modelToCadIdMap - A map of model IDs to CAD IDs retrieved from LmViewer3D.getCadIdsFromSelections.
   * @param property - The property to retrieve unique values for (e.g., cadModelCategoryId, cadMaterial, co2MaterialTypeId, totalCo2).
   * @returns A Set of unique values for the given property based on the current selections.
   */
  private getSelectedElementsByModelToCadIdMap = (
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput | null | undefined,
    modelToCadIdMap: Map<string, Set<string>>,
    property: AnalysisPropertyKey | string): Set<string | number> => {
    const values = new Set<string | number>();

    if (!input) {
      return values;
    }

    this.processInputModelData(input, selectionManager, (scenarioEntry, modelEntry, processableNodes) => {
      const modelId = getModel(modelEntry)?.apsViewId;
      if (modelId) {
        const scenarioId = scenarioEntry.scenario.scenarioId as string;
        const cadIds = Array.from(modelToCadIdMap.get(modelId) || []);

        const getCadIdForNode = (node: INodeDto) => ((node.cadId ?? "")?.replace(/\.[^/.]+$/, "") || (node.cadId ?? "")) ?? "";

        if (typeof property === 'string' && ['system', 'subSystem', 'freeText1', 'freeText2', 'freeText3'].includes(property)) {
          processableNodes.forEach((node) => {
            const cadId = getCadIdForNode(node);
            if (cadIds.includes(cadId)) {
              const attributeValue = this.getAttributeFromNode(node, property);
              if (attributeValue) values.add(attributeValue);
            }
          });
        } else {
          processableNodes.forEach((node) => {
            const cadId = getCadIdForNode(node);
            if (!cadIds.includes(cadId)) return;
            const val = property === 'co2MaterialTypeId'
              ? getNodeCo2MaterialTypeId(node, scenarioId, input!.nodeMaterialLookup, EmptyGuid)
              : (node as unknown as Record<string, unknown>)[property as string];
            if (val !== undefined && val !== null) values.add(String(val));
          });
        }
      }
    });

    console.log(`${property} values: `, values);
    return values;
  };

  private updateAnalyses = (
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput | null | undefined,
    co2MaterialTypes: Co2MaterialType[] | null | undefined,
    analysisType: AnalysisType) => {
    const scenarioIds = selectionManager.scenarioIds;
    if (scenarioIds.length === 0) {
      this.reset();
      return;
    }

    if (input) {
      this.upsertAnalysis(selectionManager, input, co2MaterialTypes, AnalysisType.Co2eIntensity, GroupType.Scenario);
      this.upsertAnalysis(selectionManager, input, co2MaterialTypes, analysisType, this.groupType);
    }
  };

  private updateSankeyAnalyses = (
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput | null | undefined,
    co2MaterialTypes: Co2MaterialType[] | null | undefined,
    options: SankeyOptions) => {


    if (!input || !co2MaterialTypes) {
      return;
    }

    const graph = {
      nodes: [],
      links: [],
    };

    const analysisData = this.getAnalysis(this.analysisType);
    if (analysisData) {
      if (options.showScenarios) {
        this.processInputScenarioData(input, selectionManager, (scenarioEntry) => {
          const s = scenarioEntry.scenario;
          this.addUniqueNode(graph, { id: s.scenarioId, label: s.name, type: 'scenarioId' });
        });
      }

      if (options.showModels) {
        this.processInputModelData(input, selectionManager, (_se, me) => {
          this.addUniqueNode(graph, { id: getModel(me)?.modelId, label: getModel(me)?.apsItemName, type: 'modelId' });
        });
      }

      if (options.showCategories) {
        this.processInputModelData(input, selectionManager, (scenarioEntry, _modelEntry, processableNodes) => {
          const categories = this.getCategoryDataFromProcessableNodes(scenarioEntry.scenario.scenarioId!, processableNodes, input.nodeMaterialLookup, input.cadModelCategories, input.materialsLoadedScenarioIds);
          categories.forEach((categoryData: CategoryData) => {
            if (options.showEmptyNodes || _.gt(categoryData.getInScopeTotalCo2(this.totalCo2Config), 0)) {
              this.addUniqueNode(graph, { id: categoryData.cadModelCategoryId, label: categoryData.label, type: 'cadModelCategoryId' });
            }
          });
        });
      }

      if (options.showCadMaterials) {
        this.processInputModelData(input, selectionManager, (_se, _me, processableNodes) => {
          const scenarioId = _se.scenario.scenarioId!;
          processableNodes.forEach((node) => {
            if (options.showEmptyNodes || _.gt(getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config), 0)) {
              const cadMaterial = node.cadMaterial || i18n.t('analysis.rumps-title.cad-material');
              this.addUniqueNode(graph, { id: cadMaterial, label: cadMaterial, type: 'cadMaterial' });
            }
          });
        });
      }

      if (options.showCo2MaterialTypes) {
        this.processInputModelData(input, selectionManager, (_se, _me, processableNodes) => {
          const scenarioId = _se.scenario.scenarioId!;
          processableNodes.forEach((node) => {
            if (options.showEmptyNodes || _.gt(getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config), 0)) {
              const co2MaterialTypeId = getNodeCo2MaterialTypeId(node, scenarioId, input.nodeMaterialLookup, EmptyGuid);
              this.addUniqueNode(graph, { id: co2MaterialTypeId, label: this.getCo2MaterialTypeName(co2MaterialTypes, co2MaterialTypeId), type: 'co2MaterialTypeId' });
            }
          });
        });
      }

      // Update node colors
      const colorPalette = DataVisualization.Colors.calcPalette(DataVisualization.Colors.MaterialsPalette, graph.nodes.length);
      graph.nodes.forEach((node: any, index: number) => {
        node.nodeColor = colorPalette[index].hex;
      });

      // Create Scenario to Model links
      if (options.showScenarios && options.showModels) {
        this.processInputModelData(input, selectionManager, (scenarioEntry, modelEntry, processableNodes) => {
          const scenarioId = scenarioEntry.scenario.scenarioId!;
          const modelCo2 = processableNodes.reduce((sum, node) => sum + (getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config) ?? 0), 0);
          if (options.showEmptyNodes || _.gt(modelCo2, 0)) {
            this.addLink(graph, { source: scenarioId, target: getModel(modelEntry)?.modelId, value: modelCo2, sourceType: 'scenarioId', targetType: 'modelId', scenarioMetadata: scenarioEntry.scenario.scenarioMetadata });
          }
        });
      }

      // Create Scenario to CadModelCategory links
      if (options.showScenarios && options.showCategories && !options.showModels) {
        this.processInputModelData(input, selectionManager, (scenarioEntry, _modelEntry, processableNodes) => {
          const scenarioId = scenarioEntry.scenario.scenarioId!;
          const categoryMap = new Map<string, number>();
          processableNodes.forEach((node) => {
            const cadModelCategoryId = node.cadModelCategoryId ?? "";
            const co2 = getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config) ?? 0;
            categoryMap.set(cadModelCategoryId, (categoryMap.get(cadModelCategoryId) ?? 0) + co2);
          });
          categoryMap.forEach((value: number, cadModelCategoryId: string) => {
            if (options.showEmptyNodes || _.gt(value, 0)) {
              this.addLink(graph, { source: scenarioId, target: cadModelCategoryId, value, sourceType: 'scenarioId', targetType: 'cadModelCategoryId', scenarioMetadata: scenarioEntry.scenario.scenarioMetadata });
            }
          });
        });
      }

      // Create Scenario to CadMaterial links
      if (options.showScenarios && options.showCadMaterials && !options.showModels && !options.showCategories) {
        this.processInputModelData(input, selectionManager, (scenarioEntry, _modelEntry, processableNodes) => {
          const scenarioId = scenarioEntry.scenario.scenarioId!;
          const cadMaterialMap = new Map<string, number>();
          processableNodes.forEach((node) => {
            const cadMaterial = node.cadMaterial || i18n.t('analysis.rumps-title.cad-material');
            const co2 = getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config) ?? 0;
            cadMaterialMap.set(cadMaterial, (cadMaterialMap.get(cadMaterial) ?? 0) + co2);
          });
          cadMaterialMap.forEach((value: number, cadMaterial: string) => {
            if (options.showEmptyNodes || _.gt(value, 0)) {
              this.addLink(graph, { source: scenarioId, target: cadMaterial, value, sourceType: 'scenarioId', targetType: 'cadMaterial', scenarioMetadata: scenarioEntry.scenario.scenarioMetadata });
            }
          });
        });
      }

      // Create Scenario to Co2MaterialType links
      if (options.showScenarios && options.showCo2MaterialTypes && !options.showModels && !options.showCategories && !options.showCadMaterials) {
        this.processInputModelData(input, selectionManager, (scenarioEntry, _modelEntry, processableNodes) => {
          const scenarioId = scenarioEntry.scenario.scenarioId!;
          const co2MaterialTypeMap = new Map<string, number>();
          processableNodes.forEach((node) => {
            const co2MaterialTypeId = getNodeCo2MaterialTypeId(node, scenarioId, input.nodeMaterialLookup, EmptyGuid);
            const co2 = getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config) ?? 0;
            co2MaterialTypeMap.set(co2MaterialTypeId, (co2MaterialTypeMap.get(co2MaterialTypeId) ?? 0) + co2);
          });
          co2MaterialTypeMap.forEach((value: number, co2MaterialTypeId: string) => {
            if (options.showEmptyNodes || _.gt(value, 0)) {
              this.addLink(graph, { source: scenarioId, target: co2MaterialTypeId, value, sourceType: 'scenarioId', targetType: 'co2MaterialTypeId', scenarioMetadata: scenarioEntry.scenario.scenarioMetadata });
            }
          });
        });
      }

      // Create Model to CadModelCategory links
      if (options.showModels && options.showCategories) {
        this.processInputModelData(input, selectionManager, (scenarioEntry, modelEntry, processableNodes) => {
          const scenarioId = scenarioEntry.scenario.scenarioId!;
          const categoryMap = new Map<string, number>();
          processableNodes.forEach((node) => {
            const cadModelCategoryId = node.cadModelCategoryId ?? "";
            const co2 = getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config) ?? 0;
            categoryMap.set(cadModelCategoryId, (categoryMap.get(cadModelCategoryId) ?? 0) + co2);
          });
          categoryMap.forEach((value: number, cadModelCategoryId: string) => {
            if (options.showEmptyNodes || _.gt(value, 0)) {
              this.addLink(graph, { source: getModel(modelEntry)?.modelId, target: cadModelCategoryId, value, sourceType: 'modelId', targetType: 'cadModelCategoryId', scenarioMetadata: scenarioEntry.scenario.scenarioMetadata });
            }
          });
        });
      }

      // Create Model to CadMaterial links
      if (options.showModels && options.showCadMaterials && !options.showCategories) {
        this.processInputModelData(input, selectionManager, (scenarioEntry, modelEntry, processableNodes) => {
          const scenarioId = scenarioEntry.scenario.scenarioId!;
          const cadMaterialMap = new Map<string, number>();
          processableNodes.forEach((node) => {
            const cadMaterial = node.cadMaterial || i18n.t('analysis.rumps-title.cad-material');
            const co2 = getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config) ?? 0;
            cadMaterialMap.set(cadMaterial, (cadMaterialMap.get(cadMaterial) ?? 0) + co2);
          });
          cadMaterialMap.forEach((value: number, cadMaterial: string) => {
            if (options.showEmptyNodes || _.gt(value, 0)) {
              this.addLink(graph, { source: getModel(modelEntry)?.modelId, target: cadMaterial, value, sourceType: 'modelId', targetType: 'cadMaterial', scenarioMetadata: scenarioEntry.scenario.scenarioMetadata });
            }
          });
        });
      }

      // Create Model to Co2MaterialType links
      if (options.showModels && options.showCo2MaterialTypes && !options.showCadMaterials && !options.showCategories) {
        this.processInputModelData(input, selectionManager, (scenarioEntry, modelEntry, processableNodes) => {
          const scenarioId = scenarioEntry.scenario.scenarioId!;
          const co2MaterialTypeMap = new Map<string, number>();
          processableNodes.forEach((node) => {
            const co2MaterialTypeId = getNodeCo2MaterialTypeId(node, scenarioId, input.nodeMaterialLookup, EmptyGuid);
            const co2 = getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config) ?? 0;
            co2MaterialTypeMap.set(co2MaterialTypeId, (co2MaterialTypeMap.get(co2MaterialTypeId) ?? 0) + co2);
          });
          co2MaterialTypeMap.forEach((value: number, co2MaterialTypeId: string) => {
            if (options.showEmptyNodes || _.gt(value, 0)) {
              this.addLink(graph, { source: getModel(modelEntry)?.modelId, target: co2MaterialTypeId, value, sourceType: 'modelId', targetType: 'co2MaterialTypeId', scenarioMetadata: scenarioEntry.scenario.scenarioMetadata });
            }
          });
        });
      }

      // Create CadModelCategory to CadMaterial links
      if (options.showCategories && options.showCadMaterials) {
        this.processInputModelData(input, selectionManager, (scenarioEntry, _modelEntry, processableNodes) => {
          const scenarioId = scenarioEntry.scenario.scenarioId!;
          const cadModelCategoryMap = new Map<string, Map<string, number>>();
          processableNodes.forEach((node) => {
            const cadModelCategoryId = node.cadModelCategoryId ?? "";
            const cadMaterial = node.cadMaterial || i18n.t('analysis.rumps-title.cad-material');
            const co2 = getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config) ?? 0;
            let cadMaterialMap = cadModelCategoryMap.get(cadModelCategoryId);
            if (!cadMaterialMap) {
              cadMaterialMap = new Map<string, number>();
              cadModelCategoryMap.set(cadModelCategoryId, cadMaterialMap);
            }
            cadMaterialMap.set(cadMaterial, (cadMaterialMap.get(cadMaterial) ?? 0) + co2);
          });
          cadModelCategoryMap.forEach((cadMaterialMap: Map<string, number>, cadModelCategoryId: string) => {
            cadMaterialMap.forEach((value: number, cadMaterial: string) => {
              if (options.showEmptyNodes || _.gt(value, 0)) {
                this.addLink(graph, { source: cadModelCategoryId, target: cadMaterial, value, sourceType: 'cadModelCategoryId', targetType: 'cadMaterial', scenarioMetadata: scenarioEntry.scenario.scenarioMetadata });
              }
            });
          });
        });
      }

      // Create CadModelCategory to Co2MaterialType links
      if (options.showCategories && options.showCo2MaterialTypes && !options.showCadMaterials) {
        this.processInputModelData(input, selectionManager, (scenarioEntry, _modelEntry, processableNodes) => {
          const scenarioId = scenarioEntry.scenario.scenarioId!;
          const cadModelCategoryMap = new Map<string, Map<string, number>>();
          processableNodes.forEach((node) => {
            const cadModelCategoryId = node.cadModelCategoryId ?? "";
            const co2MaterialTypeId = getNodeCo2MaterialTypeId(node, scenarioId, input.nodeMaterialLookup, EmptyGuid);
            const co2 = getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config) ?? 0;
            let co2MaterialTypeMap = cadModelCategoryMap.get(cadModelCategoryId);
            if (!co2MaterialTypeMap) {
              co2MaterialTypeMap = new Map<string, number>();
              cadModelCategoryMap.set(cadModelCategoryId, co2MaterialTypeMap);
            }
            co2MaterialTypeMap.set(co2MaterialTypeId, (co2MaterialTypeMap.get(co2MaterialTypeId) ?? 0) + co2);
          });
          cadModelCategoryMap.forEach((co2MaterialTypeMap: Map<string, number>, cadModelCategoryId: string) => {
            co2MaterialTypeMap.forEach((value: number, co2MaterialTypeId: string) => {
              if (options.showEmptyNodes || _.gt(value, 0)) {
                this.addLink(graph, { source: cadModelCategoryId, target: co2MaterialTypeId, value, sourceType: 'cadModelCategoryId', targetType: 'co2MaterialTypeId', scenarioMetadata: scenarioEntry.scenario.scenarioMetadata });
              }
            });
          });
        });
      }

      // Create CadMaterial to Co2MaterialType links
      if (options.showCadMaterials && options.showCo2MaterialTypes) {
        this.processInputModelData(input, selectionManager, (scenarioEntry, _modelEntry, processableNodes) => {
          const scenarioId = scenarioEntry.scenario.scenarioId!;
          const cadMaterialMap = new Map<string, Map<string, number>>();
          processableNodes.forEach((node) => {
            const cadMaterial = node.cadMaterial || i18n.t('analysis.rumps-title.cad-material');
            const co2MaterialTypeId = getNodeCo2MaterialTypeId(node, scenarioId, input.nodeMaterialLookup, EmptyGuid);
            const co2 = getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config) ?? 0;
            let co2MaterialTypeMap = cadMaterialMap.get(cadMaterial);
            if (!co2MaterialTypeMap) {
              co2MaterialTypeMap = new Map<string, number>();
              cadMaterialMap.set(cadMaterial, co2MaterialTypeMap);
            }
            co2MaterialTypeMap.set(co2MaterialTypeId, (co2MaterialTypeMap.get(co2MaterialTypeId) ?? 0) + co2);
          });
          cadMaterialMap.forEach((co2MaterialTypeMap: Map<string, number>, cadMaterial: string) => {
            co2MaterialTypeMap.forEach((value: number, co2MaterialTypeId: string) => {
              if (options.showEmptyNodes || _.gt(value, 0)) {
                this.addLink(graph, { source: cadMaterial, target: co2MaterialTypeId, value, sourceType: 'cadMaterial', targetType: 'co2MaterialTypeId', scenarioMetadata: scenarioEntry.scenario.scenarioMetadata });
              }
            });
          });
        });
      }
    }

    console.log('SankeyData: ', graph);

    // TODO: add SankyData to analyses: Map<AnalysisType, AnalysisData> = new Map();
    this.analyses.set(AnalysisType.Sankey, new SankeyData(graph) as any);
  };

  private addLink = (graph: any, link: any) => {
    // Validate that both source and target nodes exist before adding the link
    const sourceExists = graph.nodes.some((node: any) => node.id === link.source);
    const targetExists = graph.nodes.some((node: any) => node.id === link.target);

    if (!sourceExists) {
      console.warn(`Sankey link source node not found: ${link.source}`, link);
      return;
    }

    if (!targetExists) {
      console.warn(`Sankey link target node not found: ${link.target}`, link);
      return;
    }

    graph.links.push(link);
  };

  private addUniqueNode = (graph: any, node: any,) => {
    // Validate that the node has a valid ID before adding
    if (!node || !node.id) {
      console.warn('Sankey node missing ID:', node);
      return;
    }

    if (!graph.nodes.find((n: any) => n.id === node.id)) {
      graph.nodes.push(node);
    }
  };

  private hasSelection = (analysisType: AnalysisType) => {
    return this.selections.has(analysisType);
  };

  private hasAnalysisData = (analysisType: AnalysisType, groupType: GroupType) => {
    return this.analyses.get(analysisType)?.groupType === groupType;
  };

  private upsertAnalysis = (
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput,
    co2MaterialTypes: Co2MaterialType[] | null | undefined,
    analysisType: AnalysisType,
    groupType: GroupType) => {
    switch (analysisType) {
      case AnalysisType.CadModelCategory:
        if (!this.hasAnalysisData(AnalysisType.CadModelCategory, groupType)) {
          this.addCategoryAnalysis(selectionManager, input, groupType);
        }
        break;
      case AnalysisType.CadMaterial:
        if (!this.hasAnalysisData(AnalysisType.CadMaterial, groupType)) {
          this.addCadMaterialAnalysis(selectionManager, input, groupType);
        }
        break;
      case AnalysisType.CadMaterialCategory:
        if (!this.hasAnalysisData(AnalysisType.CadMaterialCategory, groupType)) {
          this.addCadMaterialCategoryAnalysis(selectionManager, input, groupType);
        }
        break;
      case AnalysisType.Co2MaterialType:
        if (!this.hasAnalysisData(AnalysisType.Co2MaterialType, groupType)) {
          this.addCo2MaterialTypeAnalysis(selectionManager, input, groupType, co2MaterialTypes);
        }
        break;
      case AnalysisType.Co2MaterialCategory:
        if (!this.hasAnalysisData(AnalysisType.Co2MaterialCategory, groupType)) {
          this.addCo2MaterialCategoryAnalysis(selectionManager, input, groupType, co2MaterialTypes);
        }
        break;
      case AnalysisType.ModelGroup:
        if (!this.hasAnalysisData(AnalysisType.ModelGroup, groupType)) {
          this.addModelGroupAnalysis(selectionManager, input, groupType);
        }
        break;
      case AnalysisType.Co2eIntensity:
        if (!this.hasAnalysisData(AnalysisType.Co2eIntensity, groupType)) {
          this.addCo2eIntensityAnalysis(selectionManager, input, groupType);
        }
        break;
      case AnalysisType.System:
        if (!this.hasAnalysisData(AnalysisType.System, groupType)) {
          this.addSystemAnalysis(selectionManager, input, groupType);
        }
        break;
      case AnalysisType.SubSystem:
        if (!this.hasAnalysisData(AnalysisType.SubSystem, groupType)) {
          this.addSubSystemAnalysis(selectionManager, input, groupType);
        }
        break;
      case AnalysisType.Attribute1:
        if (!this.hasAnalysisData(AnalysisType.Attribute1, groupType)) {
          this.addAttributeAnalysis(selectionManager, input, groupType, 'freeText1');
        }
        break;
      case AnalysisType.Attribute2:
        if (!this.hasAnalysisData(AnalysisType.Attribute2, groupType)) {
          this.addAttributeAnalysis(selectionManager, input, groupType, 'freeText2');
        }
        break;
      case AnalysisType.Attribute3:
        if (!this.hasAnalysisData(AnalysisType.Attribute3, groupType)) {
          this.addAttributeAnalysis(selectionManager, input, groupType, 'freeText3');
        }
        break;
      default:
        break;
    }
  };

  private addCo2eIntensityAnalysis = (
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput,
    groupType: GroupType) => {
    const scenarioIds = selectionManager.scenarioIds;

    const analysisData: AnalysisData = this.analyses.get(AnalysisType.Co2eIntensity) || new AnalysisData(AnalysisType.Co2eIntensity, groupType);
    this.analyses.set(AnalysisType.Co2eIntensity, analysisData);

    const colorPalette = DataVisualization.Colors.TotalCo2Palette;

    // Loop through the scenarios and models to aggregate the TotalCO2 data
    input.scenarioEntries?.forEach((scenarioEntry, scenarioIndex) => {
      const scenarioData = scenarioEntry.scenario;
      const scenarioId = scenarioData.scenarioId as string;
      if (!scenarioIds.includes(scenarioId)) return;

      const scenarioAnalysis = analysisData.scenarioAnalyses.get(scenarioId) || new ScenarioAnalysis(scenarioId, scenarioIndex, scenarioData.name!, groupType, scenarioData.scenarioMetadata?.totalFloorArea, scenarioData.scenarioMetadata?.serviceLife);
      scenarioAnalysis.groupData.length = 0;
      scenarioAnalysis.cadIdToHexColorMap.clear();
      scenarioAnalysis.groupColorMap = new Map(Array.from(Array(TOTALCO2_BUCKET_SIZE).keys()).map((key, index) => [key, colorPalette[index]]));
      analysisData.scenarioAnalyses.set(scenarioId, scenarioAnalysis);

      let groupAnalysis: GroupAnalysis;
      let buckets: Buckets;
      let numZeroTotalCo2Values = 0;
      const items: AnalysisItem[] = [];
      let ecValues: number[] = [];

      if (groupType === GroupType.Scenario) {
        groupAnalysis = new GroupAnalysis(scenarioId, scenarioIndex, scenarioData.name!, this.analysisType, groupType);
        scenarioAnalysis.groupData.push(groupAnalysis);
        numZeroTotalCo2Values = 0;
        ecValues = [];
        scenarioEntry.modelEntries?.forEach((modelEntry) => {
          const modelId = getModel(modelEntry)?.modelId;
          if (!modelId || !selectionManager.getSelectedModelIds(scenarioId).includes(modelId)) return;
          const processableNodes = getProcessableNodesForModel(modelEntry, {
            scenarioId,
            nodeSupplementalLookup: input.nodeSupplementalLookup,
            nodeMaterialLookup: input.nodeMaterialLookup,
          });
          processableNodes.forEach((node) => {
            const totalCo2 = getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config);
            if (_.gt(totalCo2, 0)) ecValues.push(totalCo2!);
            else numZeroTotalCo2Values++;
          });
        });
        buckets = this.createBuckets(ecValues, TOTALCO2_BUCKET_SIZE);
      }

      scenarioEntry.modelEntries?.forEach((modelEntry, modelIndex) => {
        const modelId = getModel(modelEntry)?.modelId;
        const urn = getModel(modelEntry)?.apsViewId as string;
        if (!modelId || !selectionManager.getSelectedModelIds(scenarioId).includes(modelId)) return;
        const processableNodes = getProcessableNodesForModel(modelEntry, {
          scenarioId,
          nodeSupplementalLookup: input.nodeSupplementalLookup,
          nodeMaterialLookup: input.nodeMaterialLookup,
        });
        if (groupType === GroupType.Model) {
          groupAnalysis = new GroupAnalysis(getModel(modelEntry)!.modelId!, modelIndex, getModel(modelEntry)!.apsItemName!, AnalysisType.Co2eIntensity, groupType);
          scenarioAnalysis.groupData.push(groupAnalysis);
          items.length = 0;
          ecValues = [];
          numZeroTotalCo2Values = 0;
          processableNodes.forEach((node) => {
            const totalCo2 = getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config);
            if (_.gt(totalCo2, 0)) ecValues.push(totalCo2!);
            else numZeroTotalCo2Values++;
          });
          buckets = this.createBuckets(ecValues, TOTALCO2_BUCKET_SIZE);
        }

        const modelCadIdMap = new Map<GroupKey, CadIds>();
        Array.from(Array(TOTALCO2_BUCKET_SIZE).keys()).forEach((bucket: number) => modelCadIdMap.set(bucket, []));
        scenarioAnalysis.modelGroupCadIdsMap.set(urn, modelCadIdMap);

        processableNodes.forEach((node) => {
          const totalCo2 = getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config);
          const cadId = ((node.cadId ?? "")?.replace(/\.[^/.]+$/, "") || (node.cadId ?? "")) ?? "";

          if (_.gt(totalCo2, 0)) {
            const bucketIndex = buckets.data.findIndex((bucket) => bucket.includes(totalCo2!));
            const hex = scenarioAnalysis.groupColorMap.get(TOTALCO2_BUCKET_SIZE - bucketIndex - 1)?.hex ?? "";
            scenarioAnalysis.cadIdToHexColorMap.set((node.cadId ?? cadId) ?? "", hex);
            (node as { hexColor?: string }).hexColor = hex;
            modelCadIdMap.get(bucketIndex)?.push(cadId);
            if ((node.cadId ?? "").includes(".")) {
              const parentCadId = (node.cadId ?? "").replace(/\.[^/.]+$/, "");
              const curHex = scenarioAnalysis.cadIdToHexColorMap.get(parentCadId);
              const newIdx = DataVisualization.Colors.TotalCo2Palette.findIndex((c: ColorDefinition) => c.hex === hex);
              const curIdx = curHex ? DataVisualization.Colors.TotalCo2Palette.findIndex((c: ColorDefinition) => c.hex === curHex) : -1;
              if (curIdx === -1 || newIdx < curIdx) scenarioAnalysis.cadIdToHexColorMap.set(parentCadId, hex);
            }
          } else {
            const hex = scenarioAnalysis.groupColorMap.get(TOTALCO2_BUCKET_SIZE - 1)?.hex ?? "";
            scenarioAnalysis.cadIdToHexColorMap.set((node.cadId ?? cadId) ?? "", hex);
            (node as { hexColor?: string }).hexColor = hex;
            modelCadIdMap.get(0)?.push(cadId);
          }
        });

        if (groupType === GroupType.Model) {
          buckets.data.forEach((ecVals: number[], index: number) => {
            const item = new AnalysisItem(index, "");
            item.elementCount = index === 0 ? numZeroTotalCo2Values : ecVals.length;
            item.co2eTotal = _.sum(ecVals);
            item.minCo2e = _.min(ecVals) || 0;
            item.maxCo2e = _.max(ecVals) || 0;
            if (item.elementCount === 0) item.name = "0";
            else if (item.minCo2e === item.maxCo2e) item.name = `${i18n.t("number", { value: item.minCo2e })} (${i18n.t("number", { value: item.co2eTotal })} tCO₂e)`;
            else item.name = `${i18n.t("number", { value: item.minCo2e })} - ${i18n.t("number", { value: item.maxCo2e })} (${i18n.t("number", { val: item.co2eTotal })} tCO₂e)`;
            items.push(item);
          });
          items.forEach((item: AnalysisItem) => groupAnalysis.items.set(item.groupKey, item));
        }

        if (groupType === GroupType.Scenario) {
          buckets.data.forEach((ecVals: number[], index: number) => {
            const item = new AnalysisItem(index, "");
            item.elementCount = index === 0 ? numZeroTotalCo2Values : ecVals.length;
            item.co2eTotal = _.sum(ecVals);
            item.minCo2e = _.min(ecVals) || 0;
            item.maxCo2e = _.max(ecVals) || 0;
            if (item.elementCount === 0) item.name = "0";
            else if (item.minCo2e === item.maxCo2e) item.name = `${i18n.t("number", { value: item.minCo2e })} (${i18n.t("number", { value: item.co2eTotal })} tCO₂e)`;
            else item.name = `${i18n.t("number", { value: item.minCo2e })} - ${i18n.t("number", { value: item.maxCo2e })} (${i18n.t("number", { val: item.co2eTotal })} tCO₂e)`;
            items.push(item);
          });
          items.forEach((item: AnalysisItem) => groupAnalysis.items.set(item.groupKey, item));
          scenarioAnalysis.co2eIntensity = groupAnalysis;
        }
      });
    });
  };

  private addCategoryAnalysis = (
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput,
    groupType: GroupType) => {
    const scenarioIds = selectionManager.scenarioIds;

    const analysisData: AnalysisData = new AnalysisData(AnalysisType.CadModelCategory, groupType);
    this.analyses.set(AnalysisType.CadModelCategory, analysisData);

    // Build category ids and labels from selected models' nodes; when scene not loaded use categories from nodeMaterialLookup (assigned materials) so ByCategoriesResults still shows data
    let cadModelCategoryIds = this.getCategoryGroupKeys(input, scenarioIds, selectionManager);
    let cadModelCategories = this.getCadModelCategories(input, scenarioIds, selectionManager);
    const colorPalette = DataVisualization.Colors.calcPalette(DataVisualization.Colors.MaterialsPalette, cadModelCategoryIds.size);

    input.scenarioEntries?.forEach((scenarioEntry, scenarioIndex) => {
      const scenarioData = scenarioEntry.scenario;
      const scenarioId = scenarioData.scenarioId as string;
      if (!scenarioIds.includes(scenarioId)) return;

      const scenarioAnalysis = new ScenarioAnalysis(scenarioId, scenarioIndex, scenarioData.name!, groupType, scenarioData.scenarioMetadata?.totalFloorArea, scenarioData.scenarioMetadata?.serviceLife!);
      scenarioAnalysis.groupColorMap = new Map(Array.from(cadModelCategoryIds).map((key, index) => [key, colorPalette[index]]));
      analysisData.scenarioAnalyses.set(scenarioId, scenarioAnalysis);

      if (groupType === GroupType.Scenario) {
        const groupAnalysis = new GroupAnalysis(scenarioId, scenarioIndex, scenarioData.name!, AnalysisType.CadModelCategory, groupType);
        cadModelCategoryIds.forEach((cadModelCategoryId: string) => groupAnalysis.items.set(cadModelCategoryId, new AnalysisItem(cadModelCategoryId, this.getCadModelCategoryName(cadModelCategories, cadModelCategoryId))));
        scenarioAnalysis.groupData.push(groupAnalysis);
      }
    });

    this.processInputModelData(input, selectionManager, (scenarioEntry, modelEntry, processableNodes) => {
      const scenarioId = scenarioEntry.scenario.scenarioId as string;
      const scenarioData = scenarioEntry.scenario;
      const scenarioIndex = input.scenarioEntries?.findIndex((e) => e.scenario.scenarioId === scenarioId) ?? 0;
      const scenarioAnalysis = analysisData.scenarioAnalyses.get(scenarioId)!;

      let groupAnalysis: GroupAnalysis;
      if (groupType === GroupType.Model) {
        groupAnalysis = new GroupAnalysis(getModel(modelEntry)!.modelId!, scenarioAnalysis.groupData.length, getModel(modelEntry)!.apsItemName!, AnalysisType.CadModelCategory, groupType);
        cadModelCategoryIds.forEach((cadModelCategoryId: string) => groupAnalysis.items.set(cadModelCategoryId, new AnalysisItem(cadModelCategoryId, this.getCadModelCategoryName(cadModelCategories, cadModelCategoryId))));
        scenarioAnalysis.groupData.push(groupAnalysis);
      } else {
        groupAnalysis = scenarioAnalysis.groupData[0] as GroupAnalysis;
      }

      const categories = this.getCategoryDataFromProcessableNodes(scenarioId, processableNodes, input.nodeMaterialLookup, cadModelCategories, input.materialsLoadedScenarioIds);
      categories.forEach((categoryData: CategoryData) => {
        const item = groupAnalysis.items.get(categoryData.cadModelCategoryId!);
        if (item) {
          item.elementCount = (item.elementCount || 0) + (categoryData.elementCount || 0);
          item.lengthTotal = (item.lengthTotal || 0) + (categoryData.lengthTotal || 0);
          item.areaTotal = (item.areaTotal || 0) + (categoryData.areaTotal || 0);
          item.volumeTotal = (item.volumeTotal || 0) + (categoryData.volumeTotal || 0);
          item.co2eTotal = (item.co2eTotal || 0) + (categoryData.getInScopeTotalCo2(this.totalCo2Config) || 0);
          item.minCo2e = Math.min(item.minCo2e, categoryData.getInScopeTotalCo2(this.totalCo2Config) || 0);
          item.maxCo2e = Math.max(item.maxCo2e, categoryData.getInScopeTotalCo2(this.totalCo2Config) || 0);
        } else {
          const newItem = new AnalysisItem(categoryData.cadModelCategoryId!, categoryData.label!);
          newItem.elementCount = categoryData.elementCount || 0;
          newItem.lengthTotal = categoryData.lengthTotal || 0;
          newItem.areaTotal = categoryData.areaTotal || 0;
          newItem.volumeTotal = categoryData.volumeTotal || 0;
          newItem.co2eTotal = categoryData.getInScopeTotalCo2(this.totalCo2Config) || 0;
          newItem.minCo2e = newItem.co2eTotal || 0;
          newItem.maxCo2e = newItem.co2eTotal || 0;
          groupAnalysis.items.set(categoryData.cadModelCategoryId!, newItem);
        }
      });
    });
  };

  private addCadMaterialAnalysis = (
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput,
    groupType: GroupType) => {
    const scenarioIds = selectionManager.scenarioIds;

    const analysisData: AnalysisData = new AnalysisData(AnalysisType.CadMaterial, groupType);
    this.analyses.set(AnalysisType.CadMaterial, analysisData);

    // Build a set of all CAD materials
    const cadMaterials = this.getCadMaterialGroupKeys(input, scenarioIds, selectionManager);
    const colorPalette = DataVisualization.Colors.calcPalette(DataVisualization.Colors.MaterialsPalette, cadMaterials.size);

    input.scenarioEntries?.forEach((scenarioEntry, scenarioIndex) => {
      const scenarioData = scenarioEntry.scenario;
      const scenarioId = scenarioData.scenarioId as string;
      if (!scenarioIds.includes(scenarioId)) return;

      const scenarioAnalysis = new ScenarioAnalysis(scenarioId, scenarioIndex, scenarioData.name!, groupType, scenarioData.scenarioMetadata?.totalFloorArea, scenarioData.scenarioMetadata?.serviceLife);
      scenarioAnalysis.groupColorMap = new Map(Array.from(cadMaterials).map((key, index) => [key, colorPalette[index]]));
      analysisData.scenarioAnalyses.set(scenarioId, scenarioAnalysis);

      let groupAnalysis: GroupAnalysis;
      const items: AnalysisItem[] = [];

      if (groupType === GroupType.Scenario) {
        groupAnalysis = new GroupAnalysis(scenarioId, scenarioIndex, scenarioData.name!, AnalysisType.CadMaterial, groupType);
        cadMaterials.forEach((cadMaterial: string) => items.push(new AnalysisItem(cadMaterial, cadMaterial)));
        scenarioAnalysis.groupData.push(groupAnalysis);
      }

      scenarioEntry.modelEntries?.forEach((modelEntry, modelIndex) => {
        const modelId = getModel(modelEntry)?.modelId;
        const urn = getModel(modelEntry)?.apsViewId as string;
        if (!modelId || !selectionManager.getSelectedModelIds(scenarioId).includes(modelId)) return;

        if (groupType === GroupType.Model) {
          groupAnalysis = new GroupAnalysis(getModel(modelEntry)!.modelId!, modelIndex, getModel(modelEntry)!.apsItemName!, AnalysisType.CadMaterial, groupType);
          items.length = 0;
          cadMaterials.forEach((cadMaterial: string) => items.push(new AnalysisItem(cadMaterial, cadMaterial)));
          scenarioAnalysis.groupData.push(groupAnalysis);
        }

        const groupCadIdsMap = new Map(Array.from(cadMaterials).map((key) => [key, [] as CadIds]));
        scenarioAnalysis.modelGroupCadIdsMap.set(urn, groupCadIdsMap);
        const rumpItem = items.find((item) => item.groupKey === i18n.t('analysis.rumps-title.cad-material'));

        const processableNodes = getProcessableNodesForModel(modelEntry, {
          scenarioId,
          nodeSupplementalLookup: input.nodeSupplementalLookup,
          nodeMaterialLookup: input.nodeMaterialLookup,
        });
        processableNodes.forEach((node) => {
          const item = node.cadMaterial ? items.find((i) => i.groupKey === node.cadMaterial) : rumpItem;
          if (item) {
            item.elementCount = item.elementCount + 1;
            const co2 = getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config) ?? 0;
            item.co2eTotal = (item.co2eTotal || 0) + co2;
            item.minCo2e = Math.min(item.minCo2e, co2);
            item.maxCo2e = Math.max(item.maxCo2e, co2);
            const cadId = ((node.cadId ?? "")?.replace(/\.[^/.]+$/, "") || (node.cadId ?? "")) ?? "";
            groupCadIdsMap.get(item.groupKey as string)?.push(cadId);
          } else {
            console.error(`CadMaterial not found: ${node.cadMaterial}`);
          }
        });

        if (groupType === GroupType.Model) {
          items.forEach((item) => groupAnalysis.items.set(item.groupKey, item));
        }
      });

      if (groupType === GroupType.Scenario) {
        items.forEach((item) => groupAnalysis.items.set(item.groupKey, item));
      }
    });
  };

  private addCadMaterialCategoryAnalysis = (
    _selectionManager: SelectionManager,
    _input: ProjectAnalysisInput,
    _groupType: GroupType) => {
  };

  private addCo2MaterialTypeAnalysis = (
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput,
    groupType: GroupType,
    co2MaterialTypes: Co2MaterialType[] | null | undefined) => {
    const scenarioIds = selectionManager.scenarioIds;

    const analysisData: AnalysisData = new AnalysisData(AnalysisType.Co2MaterialType, groupType);
    this.analyses.set(AnalysisType.Co2MaterialType, analysisData);

    // Build a set of all co2MaterialTypes
    const co2MaterialTypeIds = this.getCo2MaterialTypeGroupKeys(input, scenarioIds, selectionManager);
    const colorPalette = DataVisualization.Colors.calcPalette(DataVisualization.Colors.MaterialsPalette, co2MaterialTypeIds.size);

    input.scenarioEntries?.forEach((scenarioEntry, scenarioIndex) => {
      const scenarioData = scenarioEntry.scenario;
      const scenarioId = scenarioData.scenarioId as string;
      if (!scenarioIds.includes(scenarioId)) return;

      const scenarioAnalysis = new ScenarioAnalysis(scenarioId, scenarioIndex, scenarioData.name!, groupType, scenarioData.scenarioMetadata?.totalFloorArea, scenarioData.scenarioMetadata?.serviceLife);
      analysisData.scenarioAnalyses.set(scenarioId, scenarioAnalysis);
      scenarioAnalysis.groupColorMap = new Map(Array.from(co2MaterialTypeIds).map((key, index) => [key, colorPalette[index]]));

      let groupAnalysis: GroupAnalysis;
      const items: AnalysisItem[] = [];

      if (groupType === GroupType.Scenario) {
        groupAnalysis = new GroupAnalysis(scenarioId, scenarioIndex, scenarioData.name!, AnalysisType.Co2MaterialType, groupType);
        co2MaterialTypeIds.forEach((co2MaterialTypeId: string) => items.push(new AnalysisItem(co2MaterialTypeId, this.getCo2MaterialTypeName(co2MaterialTypes, co2MaterialTypeId))));
        scenarioAnalysis.groupData.push(groupAnalysis);
      }

      scenarioEntry.modelEntries?.forEach((modelEntry, modelIndex) => {
        const modelId = getModel(modelEntry)?.modelId;
        const urn = getModel(modelEntry)?.apsViewId as string;
        if (!modelId || !selectionManager.getSelectedModelIds(scenarioId).includes(modelId)) return;

        if (groupType === GroupType.Model) {
          groupAnalysis = new GroupAnalysis(getModel(modelEntry)!.modelId!, modelIndex, getModel(modelEntry)!.apsItemName!, AnalysisType.Co2MaterialType, groupType);
          items.length = 0;
          co2MaterialTypeIds.forEach((co2MaterialTypeId: string) => items.push(new AnalysisItem(co2MaterialTypeId, this.getCo2MaterialTypeName(co2MaterialTypes, co2MaterialTypeId))));
          scenarioAnalysis.groupData.push(groupAnalysis);
        }

        const groupCadIdsMap = new Map(Array.from(co2MaterialTypeIds).map((key) => [key, [] as CadIds]));
        scenarioAnalysis.modelGroupCadIdsMap.set(urn, groupCadIdsMap);
        const rumpItem = items.find((item) => item.groupKey === EmptyGuid);

        const processableNodes = getProcessableNodesForModel(modelEntry, {
          scenarioId,
          nodeSupplementalLookup: input.nodeSupplementalLookup,
          nodeMaterialLookup: input.nodeMaterialLookup,
        });
        processableNodes.forEach((node) => {
          const co2MaterialTypeId = getNodeCo2MaterialTypeId(node, scenarioId, input.nodeMaterialLookup, EmptyGuid);
          const item = co2MaterialTypeId ? items.find((i) => i.groupKey === co2MaterialTypeId) : rumpItem;
          if (item) {
            item.elementCount = item.elementCount + 1;
            const co2 = getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config) ?? 0;
            item.co2eTotal = (item.co2eTotal || 0) + co2;
            item.minCo2e = Math.min(item.minCo2e, co2);
            item.maxCo2e = Math.max(item.maxCo2e, co2);
            const cadId = ((node.cadId ?? "")?.replace(/\.[^/.]+$/, "") || (node.cadId ?? "")) ?? "";
            groupCadIdsMap.get(item.groupKey as string)?.push(cadId);
          } else {
            console.error(`Co2MaterialType not found: ${co2MaterialTypeId}`);
          }
        });

        if (groupType === GroupType.Model) {
          items.forEach((item) => groupAnalysis.items.set(item.groupKey, item));
        }
      });

      if (groupType === GroupType.Scenario) {
        items.forEach((item) => groupAnalysis.items.set(item.groupKey, item));
      }
    });
  };

  private addCo2MaterialCategoryAnalysis = (
    _selectionManager: SelectionManager,
    _input: ProjectAnalysisInput,
    _groupType: GroupType,
    _co2MaterialTypes: Co2MaterialType[] | null | undefined) => {
  };

  private addModelGroupAnalysis = (_selectionManager: SelectionManager, _input: ProjectAnalysisInput, _groupType: GroupType) => {
  };

  private getCadModelCategoryName = (cadModelCategories: Map<string, string>, cadModelCategoryId: string) => {
    if (cadModelCategoryId === EmptyGuid) {
      return i18n.t('analysis.rumps-title.co2-material-type');
    }

    const cadModelCategoryName = cadModelCategories.get(cadModelCategoryId);
    return cadModelCategoryName || cadModelCategoryId;
  };

  private getCo2MaterialTypeName = (co2MaterialTypes: Co2MaterialType[] | null | undefined, co2MaterialTypeId: string) => {
    if (co2MaterialTypeId === EmptyGuid) {
      return i18n.t('analysis.rumps-title.co2-material-type');
    }

    const co2MaterialType = co2MaterialTypes?.find((co2MaterialType: Co2MaterialType) => co2MaterialType.co2MaterialTypeId === co2MaterialTypeId);
    return co2MaterialType?.name || co2MaterialTypeId;
  };

  /**
   * Build CategoryData[] from processable nodes (group by cadModelCategoryId) for use where modelData.categories was used.
   */
  private getCategoryDataFromProcessableNodes = (
    scenarioId: string,
    processableNodes: INodeDto[],
    nodeMaterialLookup: Map<string, NodeMaterialDto>,
    categoryLabelLookup?: Map<string, string>,
    materialsLoadedScenarioIds?: ReadonlySet<string>
  ): CategoryData[] => {
    if (materialsLoadedScenarioIds && !materialsLoadedScenarioIds.has(scenarioId)) {
      return [];
    }
    type Agg = { count: number; lengthTotal: number; areaTotal: number; volumeTotal: number; co2ByField: Record<string, number> };
    const byCategory = new Map<string, Agg>();
    for (const node of processableNodes) {
      const cadModelCategoryId = node.cadModelCategoryId ?? "";
      const cur = byCategory.get(cadModelCategoryId);
      const co2ByField: Record<string, number> = { ...cur?.co2ByField };
      for (const field of this.totalCo2Config) {
        const v = getNodeInScopeTotalCo2(node, scenarioId, nodeMaterialLookup, [field]) ?? 0;
        co2ByField[field] = (co2ByField[field] ?? 0) + v;
      }
      const len = (node.length ?? 0) + (cur?.lengthTotal ?? 0);
      const area = (node.area ?? 0) + (cur?.areaTotal ?? 0);
      const vol = (node.volume ?? 0) + (cur?.volumeTotal ?? 0);
      byCategory.set(cadModelCategoryId, {
        count: (cur?.count ?? 0) + 1,
        lengthTotal: len,
        areaTotal: area,
        volumeTotal: vol,
        co2ByField,
      });
    }
    const result: CategoryData[] = [];
    byCategory.forEach((agg, cadModelCategoryId) => {
      const cat = new CategoryData();
      cat.cadModelCategoryId = cadModelCategoryId;
      cat.label = categoryLabelLookup?.get(cadModelCategoryId) ?? cadModelCategoryId;
      cat.elementCount = agg.count;
      cat.lengthTotal = agg.lengthTotal;
      cat.areaTotal = agg.areaTotal;
      cat.volumeTotal = agg.volumeTotal;
      (cat as { co2Aggregates?: Record<string, number> }).co2Aggregates = agg.co2ByField;
      result.push(cat);
    });
    return result;
  };

  private getCadModelCategories = (
    input: ProjectAnalysisInput,
    _scenarioIds: string[],
    selectionManager: SelectionManager
  ) => {
    const cadModelCategories = new Map<string, string>();
    this.processInputModelData(input, selectionManager, (scenarioEntry, _modelEntry, processableNodes) => {
      const scenarioId = scenarioEntry.scenario.scenarioId as string;
      const categories = this.getCategoryDataFromProcessableNodes(
        scenarioId,
        processableNodes,
        input.nodeMaterialLookup,
        input.cadModelCategories,
        input.materialsLoadedScenarioIds,
      );
      categories.forEach((categoryData: CategoryData) => {
        cadModelCategories.set(categoryData.cadModelCategoryId!, categoryData.label!);
      });
    });
    return cadModelCategories;
  };

  private getCategoryGroupKeys = (
    input: ProjectAnalysisInput,
    _scenarioIds: string[],
    selectionManager: SelectionManager) => {
    const cadModelCategoryIds = new Set<string>();
    this.processInputModelData(input, selectionManager, (_scenarioEntry, _modelEntry, processableNodes) => {
      processableNodes.forEach((node) => {
        if (node.cadModelCategoryId) cadModelCategoryIds.add(node.cadModelCategoryId);
      });
    });
    return cadModelCategoryIds;
  };

  private getCadModelCategoriesFromMaterialsFallback = (
    categoryIds: Set<string>,
    apiCategories?: Map<string, string>
  ): Map<string, string> => {
    const map = new Map<string, string>();
    categoryIds.forEach((id) => {
      map.set(id, apiCategories?.get(id) ?? id);
    });
    return map;
  };

  /** When category analysis used materials fallback (no scene nodes), fill scenario-level group items from nodeMaterialLookup. Only applies to GroupType.Scenario (we cannot attribute materials to a specific model without nodes). */
  private aggregateCategoryAnalysisFromMaterials = (
    input: ProjectAnalysisInput,
    analysisData: AnalysisData,
    scenarioIds: string[]
  ): void => {
    if (analysisData.groupType !== GroupType.Scenario) return;
    scenarioIds.forEach((scenarioId) => {
      const scenarioAnalysis = analysisData.scenarioAnalyses.get(scenarioId);
      if (!scenarioAnalysis?.groupData?.length) return;
      const groupAnalysis = scenarioAnalysis.groupData[0];
      input.nodeMaterialLookup.forEach((material: NodeMaterialDto, key: string) => {
        if (!key.startsWith(scenarioId + ":")) return;
        const categoryId = material.cadModelCategoryId;
        if (!categoryId) return;
        const item = groupAnalysis.items.get(categoryId);
        if (!item) return;
        const co2 = this.getMaterialInScopeTotalCo2(material);
        item.elementCount = (item.elementCount || 0) + 1;
        item.co2eTotal = (item.co2eTotal || 0) + co2;
        item.minCo2e = Math.min(item.minCo2e ?? co2, co2);
        item.maxCo2e = Math.max(item.maxCo2e ?? co2, co2);
      });
    });
  };

  private getMaterialInScopeTotalCo2 = (material: NodeMaterialDto): number => {
    const rec = material as unknown as Record<string, number | undefined>;
    return this.totalCo2Config.reduce((sum, field) => sum + (Number(rec?.[field]) || 0), 0);
  };

  private getCadMaterialGroupKeys = (
    input: ProjectAnalysisInput,
    _scenarioIds: string[],
    selectionManager: SelectionManager) => {
    const cadMaterials = new Set<string>();
    this.processInputModelData(input, selectionManager, (_scenarioEntry, _modelEntry, processableNodes) => {
      processableNodes.forEach((node) => {
        if (node.cadMaterial) cadMaterials.add(node.cadMaterial);
      });
    });
    cadMaterials.add(i18n.t('analysis.rumps-title.cad-material'));
    return cadMaterials;
  };

  private getCo2MaterialTypeGroupKeys = (
    input: ProjectAnalysisInput,
    _scenarioIds: string[],
    selectionManager: SelectionManager) => {
    const co2MaterialTypes = new Set<string>();
    this.processInputModelData(input, selectionManager, (scenarioEntry, _modelEntry, processableNodes) => {
      const scenarioId = scenarioEntry.scenario.scenarioId as string;
      processableNodes.forEach((node) => {
        const id = getNodeCo2MaterialTypeId(node, scenarioId, input.nodeMaterialLookup, EmptyGuid);
        if (id) co2MaterialTypes.add(id);
      });
    });
    co2MaterialTypes.add(EmptyGuid);
    return co2MaterialTypes;
  };

  private processInputScenarioData = (
    input: ProjectAnalysisInput,
    selectionManager: SelectionManager,
    callback: (scenarioEntry: ProjectAnalysisScenarioEntry) => void) => {
    const scenarioIds = selectionManager.scenarioIds;
    input.scenarioEntries?.forEach((scenarioEntry: ProjectAnalysisScenarioEntry) => {
      const scenarioId = scenarioEntry.scenario.scenarioId as string;
      if (scenarioId && scenarioIds.includes(scenarioId)) {
        callback(scenarioEntry);
      }
    });
  };

  private processInputModelData = (
    input: ProjectAnalysisInput,
    selectionManager: SelectionManager,
    callback: (scenarioEntry: ProjectAnalysisScenarioEntry, modelEntry: ProjectAnalysisModelEntry, processableNodes: INodeDto[]) => void) => {
    const scenarioIds = selectionManager.scenarioIds;
    input.scenarioEntries?.forEach((scenarioEntry: ProjectAnalysisScenarioEntry) => {
      const scenarioId = scenarioEntry.scenario.scenarioId as string;
      if (!scenarioId || !scenarioIds.includes(scenarioId)) return;
      if (!input.materialsLoadedScenarioIds?.has(scenarioId)) return;
      const selectedModelIds = selectionManager.getSelectedModelIds(scenarioId);
      scenarioEntry.modelEntries?.forEach((modelEntry: ProjectAnalysisModelEntry) => {
        const modelId = getModel(modelEntry)?.modelId;
        if (!modelId || !selectedModelIds.includes(modelId)) return;
        const processableNodes = getProcessableNodesForModel(modelEntry, {
          scenarioId,
          nodeSupplementalLookup: input.nodeSupplementalLookup,
          nodeMaterialLookup: input.nodeMaterialLookup,
        });
        callback(scenarioEntry, modelEntry, processableNodes);
      });
    });
  };

  private getAttributeFromNode = (node: INodeDto, attributeField: string): string | null => {
    switch (attributeField) {
      case "system":
        return node.systemId ?? null;
      case "subSystem":
        return node.subSystemId ?? null;
      case "freeText1":
        return node.freeText1 ?? null;
      case "freeText2":
        return node.freeText2 ?? null;
      case "freeText3":
        return node.freeText3 ?? null;
      default:
        return null;
    }
  };

  private createBuckets = (ecValues: number[], numBuckets: number) => {
    // We reserve the 
    const buckets = bucketize(ecValues, numBuckets - 1);
    buckets.data.unshift([0]);
    return buckets;
  };

  private addSystemAnalysis = (
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput,
    groupType: GroupType) => {
    this.addAttributeAnalysis(selectionManager, input, groupType, 'system');
  };

  private addSubSystemAnalysis = (
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput,
    groupType: GroupType) => {
    this.addAttributeAnalysis(selectionManager, input, groupType, 'subSystem');
  };

  private addAttributeAnalysis = (
    selectionManager: SelectionManager,
    input: ProjectAnalysisInput,
    groupType: GroupType,
    attributeField: 'system' | 'subSystem' | 'freeText1' | 'freeText2' | 'freeText3') => {
    const scenarioIds = selectionManager.scenarioIds;
    if (scenarioIds.length === 0) {
      return;
    }

    const analysisType = this.getAnalysisTypeFromAttributeField(attributeField);
    console.log('Creating attribute analysis:', { attributeField, analysisType, groupType });
    const analysisData: AnalysisData = new AnalysisData(analysisType, groupType);
    this.analyses.set(analysisType, analysisData);

    // Get all unique attribute values
    const attributeValues = this.getAllAttributeValues(input, scenarioIds, selectionManager, attributeField);
    console.log('Found attribute values:', { attributeField, count: attributeValues.size, values: Array.from(attributeValues) });
    const colorPalette = DataVisualization.Colors.calcPalette(DataVisualization.Colors.MaterialsPalette, attributeValues.size);

    input.scenarioEntries?.forEach((scenarioEntry, scenarioIndex) => {
      const scenarioData = scenarioEntry.scenario;
      const scenarioId = scenarioData.scenarioId as string;
      if (!scenarioIds.includes(scenarioId)) return;

      const scenarioAnalysis = new ScenarioAnalysis(scenarioId, scenarioIndex, scenarioData.name!, groupType);
      scenarioAnalysis.groupColorMap = new Map(Array.from(attributeValues).map((key, index) => [key, colorPalette[index]]));
      analysisData.scenarioAnalyses.set(scenarioId, scenarioAnalysis);

      let groupAnalysis: GroupAnalysis;
      const items: AnalysisItem[] = [];

      if (groupType === GroupType.Scenario) {
        groupAnalysis = new GroupAnalysis(scenarioId, scenarioIndex, scenarioData.name!, analysisType, groupType);
        attributeValues.forEach((value: string) => {
          items.push(new AnalysisItem(value, this.getAttributeDisplayName(value, attributeField)));
        });
        scenarioAnalysis.groupData.push(groupAnalysis);
      }

      scenarioEntry.modelEntries?.forEach((modelEntry, modelIndex) => {
        const modelId = getModel(modelEntry)?.modelId;
        const urn = getModel(modelEntry)?.apsViewId as string;
        if (!modelId || !selectionManager.getSelectedModelIds(scenarioId).includes(modelId)) return;

        if (groupType === GroupType.Model) {
          groupAnalysis = new GroupAnalysis(getModel(modelEntry)!.modelId!, modelIndex, getModel(modelEntry)!.apsItemName!, analysisType, groupType);
          items.length = 0;
          attributeValues.forEach((value: string) => {
            items.push(new AnalysisItem(value, this.getAttributeDisplayName(value, attributeField)));
          });
          scenarioAnalysis.groupData.push(groupAnalysis);
        }

        const groupCadIdsMap = new Map(Array.from(attributeValues).map((key) => [key, [] as CadIds]));
        scenarioAnalysis.modelGroupCadIdsMap.set(urn, groupCadIdsMap);

        const processableNodes = getProcessableNodesForModel(modelEntry, {
          scenarioId,
          nodeSupplementalLookup: input.nodeSupplementalLookup,
          nodeMaterialLookup: input.nodeMaterialLookup,
        });
        processableNodes.forEach((node) => {
          const attributeValue = this.getAttributeFromNode(node, attributeField);
          if (!attributeValue) return;

          const item = items.find((i) => i.groupKey === attributeValue);
          if (item) {
            item.elementCount = item.elementCount + 1;
            const co2 = getNodeInScopeTotalCo2(node, scenarioId, input.nodeMaterialLookup, this.totalCo2Config) ?? 0;
            item.co2eTotal = (item.co2eTotal || 0) + co2;
            item.minCo2e = Math.min(item.minCo2e, co2);
            item.maxCo2e = Math.max(item.maxCo2e, co2);
            const cadId = ((node.cadId ?? "")?.replace(/\.[^/.]+$/, "") || (node.cadId ?? "")) ?? "";
            groupCadIdsMap.get(attributeValue)?.push(cadId);
          }
        });

        if (groupType === GroupType.Model) {
          items.forEach((item) => groupAnalysis.items.set(item.groupKey, item));
        }
      });

      if (groupType === GroupType.Scenario) {
        items.forEach((item) => groupAnalysis.items.set(item.groupKey, item));
      }
    });
  };

  private getAnalysisTypeFromAttributeField = (attributeField: string): AnalysisType => {
    switch (attributeField) {
      case 'system':
        return AnalysisType.System;
      case 'subSystem':
        return AnalysisType.SubSystem;
      case 'freeText1':
        return AnalysisType.Attribute1;
      case 'freeText2':
        return AnalysisType.Attribute2;
      case 'freeText3':
        return AnalysisType.Attribute3;
      default:
        return AnalysisType.System;
    }
  };

  private getAllAttributeValues = (
    input: ProjectAnalysisInput,
    _scenarioIds: string[],
    selectionManager: SelectionManager,
    attributeField: 'system' | 'subSystem' | 'freeText1' | 'freeText2' | 'freeText3'): Set<string> => {
    const values = new Set<string>();
    this.processInputModelData(input, selectionManager, (_se, _me, processableNodes) => {
      processableNodes.forEach((node) => {
        const value = this.getAttributeFromNode(node, attributeField);
        if (value) values.add(value);
      });
    });
    return values;
  };

  private getAttributeValue = (node: INodeDto, attributeField: string): string | null => {
    return this.getAttributeFromNode(node, attributeField);
  };

  private getAttributeDisplayName = (value: string, _attributeField: string): string => {
    // Name resolution is now handled in the UI components using metadata entities
    return value;
  };

  private reset = () => {
    this.analyses.clear();
    this.selections.clear();
  };

  /** Clears all analyses and selections and emits so charts/grids show empty. Call when there is no selection. */
  clearAnalysesAndSelections = () => {
    this.reset();
    this.emitter.emit(ProjectAnalysisEvents.ANALYSIS_CHANGED_EVENT);
  };
}

/**
 * Helper class to configure which TotalCo2 columns to include in the analysis.
 * The configuration is based on the columns selected in the UI.
 */
export class TotalCo2Configurator {
  a1A2A3: string[] = ['a1A2A3'];
  a4A5: string[] = ['a4', 'a5'];
  a: string[] = ['a1A2A3', 'a4', 'a5'];
  b1: string[] = ['b1'];
  b2B5: string[] = ['b2', 'b3', 'b4', 'b5'];
  biogenic: string[] = ['biogenic'];
  b: string[] = ['b1', 'b2', 'b3', 'b4', 'b5', 'b6', 'b7', 'biogenic'];
  c: string[] = ['c1', 'c2', 'c3', 'c4'];
  d: string[] = ['d'];
  totalCo2: string[] = ['totalCo2'];

  getTotalCo2Config = (columns: string[]): string[] => {
    if (columns.includes('totalCo2')) {
      return this.totalCo2;
    }

    return Array.from(columns.reduce((acc: Set<string>, col: string) => {
      const values = this[col as keyof TotalCo2Configurator] as string[];
      values.forEach(acc.add, acc);
      return acc;
    }, new Set<string>));
  };
}

export const projectAnalysis = new ProjectAnalysis();